import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from "@mui/material";
import VideoPlayer from "./VideoPlayer"; // Video Player Component
import { getSensorDataById } from "../../api"; // API Call
import Pagination from '../common/Pagination';

const GenericVideoCamera = ({ sensorId }) => {
  const [sensorData, setSensorData] = useState([]);
  const [liveVideo, setLiveVideo] = useState(null);
  const [selectedClip, setSelectedClip] = useState(null);
  const [customDate, setCustomDate] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!sensorId) return;

        const response = await getSensorDataById(sensorId);
        const data = response.data.data || [];

        if (!Array.isArray(data)) {
          console.error("Unexpected sensor data format:", data);
          setSensorData([]);
          setLiveVideo(null);
          return;
        }

        // Get live stream (if within the current time range)
        const now = new Date();
        const liveStream = data.find(
          (clip) =>
            new Date(clip.data.startTimestamp) <= now &&
            new Date(clip.data.endTimestamp) >= now
        );

        setLiveVideo(liveStream ? liveStream : null);
        setSensorData(data);
      } catch (error) {
        console.error("Error fetching camera data:", error);
        setSensorData([]);
        setLiveVideo(null);
      }
    };

    fetchData();
  }, [sensorId]);

  const handleDateSearch = () => {
    if (!customDate) return;

    const selected = sensorData.find(
      (clip) =>
        new Date(clip.data.startTimestamp).toISOString().split("T")[0] ===
        customDate
    );

    if (selected) {
      setSelectedClip(selected);
    } else {
      setSelectedClip(null);
      setSnackbarMessage("No recorded videos found for the selected date.");
      setSnackbarOpen(true);
    }
    setIsDialogOpen(false);
  };

  const handleLiveFeedClick = () => {
    if (liveVideo) {
      setSelectedClip(liveVideo);
    } else {
      setSelectedClip(null); // Clear any selected clip
      setSnackbarMessage("Live Feed is not available.");
      setSnackbarOpen(true);
    }
  };

  const handleClipSelection = (clip) => {
    setSelectedClip(prev => prev?._id === clip._id ? null : clip);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Calculate pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const totalPages = Math.ceil(sensorData.length / itemsPerPage);
  const paginatedData = sensorData.slice(startIndex, endIndex);

  return (
    <Box display="flex" height="500px" border="1px solid #ddd" borderRadius="8px" overflow="hidden">
      {/* Left Sidebar - Modified structure */}
      <Box 
        width="300px" 
        bgcolor="#1e1e1e" // Darker background for sidebar
        display="flex" 
        flexDirection="column"
      >
        {/* Main content area with scroll */}
        <Box flex="1" p={2} overflow="auto">
          <Typography variant="h6" gutterBottom color="white">
            Camera Feeds
          </Typography>

          <List>
            {/* Live Video Option (always included) */}
            <ListItem
              button
              onClick={handleLiveFeedClick}
              sx={{
                backgroundColor: selectedClip && liveVideo && selectedClip._id === liveVideo._id ? "#2c2c2c" : "transparent",
                color: "white",
                '&:hover': {
                  backgroundColor: '#2c2c2c',
                  cursor: 'pointer',
                },
                borderRadius: '4px',
                mb: 0.5,
              }}
            >
              <ListItemText 
                primary="Live Feed"
                primaryTypographyProps={{
                  style: { color: 'white' }
                }}
              />
            </ListItem>

            {/* History Clips */}
            {paginatedData.map((clip, index) => (
              <ListItem
                button
                key={clip._id}
                onClick={() => handleClipSelection(clip)}
                sx={{
                  backgroundColor: selectedClip && selectedClip._id === clip._id ? "#2c2c2c" : "transparent",
                  color: "white",
                  '&:hover': {
                    backgroundColor: '#2c2c2c',
                    cursor: 'pointer',
                  },
                  borderRadius: '4px',
                  mb: 0.5,
                }}
              >
                <ListItemText
                  primary={`Clip ${startIndex + index + 1}`}
                  secondary={`${new Date(
                    clip.data.startTimestamp
                  ).toLocaleString()} - ${new Date(
                    clip.data.endTimestamp
                  ).toLocaleString()}`}
                  primaryTypographyProps={{
                    style: { color: 'white' }
                  }}
                  secondaryTypographyProps={{
                    style: { color: '#a0a0a0' }
                  }}
                />
              </ListItem>
            ))}

            {/* Search Custom Time */}
            <ListItem 
              button 
              onClick={() => setIsDialogOpen(true)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: '#2c2c2c',
                  cursor: 'pointer',
                },
                borderRadius: '4px',
              }}
            >
              <ListItemText 
                primary="Search by Date"
                primaryTypographyProps={{
                  style: { color: 'white' }
                }}
              />
            </ListItem>
          </List>
        </Box>

        {/* Pagination fixed at bottom */}
        <Box 
          borderTop="1px solid #2c2c2c" 
          p={1} 
          bgcolor="white"
          sx={{
            '& .MuiIconButton-root': {
              color: '#1e1e1e',
              '&.Mui-disabled': {
                color: 'rgba(0, 0, 0, 0.26)'
              },
              '&:hover': {
                cursor: 'pointer',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            },
            '& .MuiTypography-root': {
              color: '#1e1e1e'
            }
          }}
        >
          {sensorData.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </Box>
      </Box>

      {/* Right Video Player */}
      <Box flex={1} p={2} display="flex" justifyContent="center" alignItems="center" bgcolor="#000">
        {selectedClip ? (
          <VideoPlayer key={selectedClip._id} url={selectedClip.data.videoUrl} />
        ) : (
          <Typography variant="h6" color="white" align="center">
            {liveVideo === null && selectedClip === null ? 
              "Live Feed is not available.\nPlease select a recorded video to play." :
              "Select a video to play"
            }
          </Typography>
        )}
      </Box>

      {/* Custom Date Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogTitle>Select Date</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            fullWidth
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDateSearch} color="primary">
            Search
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for alerts */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default GenericVideoCamera;
